import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import {number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize} from "@pg-design/helpers-css";
import {Link} from "@pg-design/link-module";
import {CheckboxControlled, InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {IAppError} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../../../../../app/rp_reducer";
import {RodoFormInfo} from "../../../../../../../application/components/RodoFormInfo";
import {useGetVendorDetailApplicationQuery} from "../../../../../../../vendor/api/get_vendor_detail";
import {askForPriceleadButtonCopy} from "../../../../../../constants/lead_button_copy";
import {useOpenedLeadModalRequiredParams} from "../../../../../../hooks/use_lead_modal_required_params";
import {ILeadModalPriceConversationChatLeadFormHandleSubmit} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormHandleSubmit";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormValues";

interface IProps {
    showForm: boolean;
    initialValues: ILeadModalPriceConversationChatLeadFormValues;
    disabled: boolean;
    handleSubmit: ILeadModalPriceConversationChatLeadFormHandleSubmit;
    handleFormInteraction: () => void;
    handleChangeData: () => void;
}

export function LeadModalPriceConversationChatLeadForm(props: IProps) {
    const {initialValues, disabled, handleSubmit, handleFormInteraction, showForm, handleChangeData} = props;

    const {vendorId} = useOpenedLeadModalRequiredParams("property");
    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId);
    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);

    const form = useForm<ILeadModalPriceConversationChatLeadFormValues>({
        disabled,
        defaultValues: initialValues,
        resolver: yupResolver(formSchema),
        mode: "onBlur"
    });

    const onSubmit = async (formValues: ILeadModalPriceConversationChatLeadFormValues) => {
        handleSubmit(formValues, (error) => {
            //show form when logged user has blocked email
            if (isValidationError(error) && !showForm) {
                handleChangeData();
            }
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    form.setError(fieldName as keyof ILeadModalPriceConversationChatLeadFormValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        });
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} css={fields}>
                {(!showForm && !initialValues) || showForm ? (
                    <>
                        <InputField
                            name="name"
                            placeholder="Imię i nazwisko"
                            onClick={handleFormInteraction}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT}
                        />
                        <InputField name="email" placeholder="E-mail" onClick={handleFormInteraction} dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT} />
                        <PhoneFieldControlled
                            name="phone"
                            codes={codes ?? []}
                            onClick={handleFormInteraction}
                            placeholder="Numer telefonu"
                            maxMenuHeight={200}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        />
                    </>
                ) : (
                    <div css={filledData}>
                        <Text variant="mini_header">Twoje dane</Text>
                        <Text variant="body_copy_2">
                            Imię i nazwisko: <strong>{initialValues.name}</strong>
                            <br />
                            e-mail: <strong>{initialValues.email}</strong>
                            <br />
                            Telefon: <strong>{initialValues.phone.number}</strong>
                        </Text>
                        <Link css={linkDecoration} onClick={handleChangeData}>
                            Zmień dane
                        </Link>
                    </div>
                )}
                <div css={optins}>
                    {(showForm || (!showForm && !initialValues.new_property_notification_consent)) && (
                        <CheckboxControlled
                            name="new_property_notification_consent"
                            id="new_property_notification_consent"
                            labelContent={
                                <Text variant="info_txt_2">
                                    Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.
                                </Text>
                            }
                            checkedMarkColor="#02D054"
                        />
                    )}
                    {(showForm || (!showForm && !initialValues.financing_services)) && (
                        <CheckboxControlled
                            name="financing_services"
                            id="financing_services"
                            labelContent={<Text variant="info_txt_2">Poproszę o przedstawienie oferty kredytu na to mieszkanie.</Text>}
                            checkedMarkColor="#02D054"
                        />
                    )}
                </div>
                <Button
                    variant="filled_primary"
                    type="submit"
                    css={button}
                    onClick={handleFormInteraction}
                    disabled={disabled}
                    isLoading={disabled}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {askForPriceleadButtonCopy}
                </Button>
                <RodoFormInfo vendor={vendorDetail} css={rodo} />
                <Text variant="info_txt_2" as="span" css={terms}>
                    Akceptuję{" "}
                    <Link href={termsOfService} size="inherit" target="_blank" css={linkDecoration}>
                        regulamin serwisu.
                    </Link>
                </Text>
            </form>
        </FormProvider>
    );
}

// Utils
const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()})
});

function isValidationError(error: unknown): error is {appError: IAppError} {
    return typeof error === "object" && error !== null && "appError" in error && (error.appError as IAppError).status === 400;
}

const termsOfService = rpAppLink.termsOfService.base();

// Styles
const fields = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const optins = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
    padding: ${calculateRemSize(1)} 0;
`;

const rodo = css`
    padding: 0;
    margin: 0 0 ${calculateRemSize(1)} 0;
`;

const terms = css`
    text-align: center;
`;

const linkDecoration = css`
    text-decoration: underline;
`;

const button = css`
    height: ${calculateRemSize(5)};
`;

const filledData = (theme: Theme) => css`
    background: ${theme.colors.background[100]};
    padding: ${calculateRemSize(2)};
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
    ${borderRadius(2)}
`;
import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import {number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize} from "@pg-design/helpers-css";
import {Link} from "@pg-design/link-module";
import {CheckboxControlled, InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {IAppError} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../../../../../../app/rp_reducer";
import {RodoFormInfo} from "../../../../../../../application/components/RodoFormInfo";
import {useGetVendorDetailApplicationQuery} from "../../../../../../../vendor/api/get_vendor_detail";
import {askForPriceleadButtonCopy} from "../../../../../../constants/lead_button_copy";
import {useOpenedLeadModalRequiredParams} from "../../../../../../hooks/use_lead_modal_required_params";
import {ILeadModalPriceConversationChatLeadFormHandleSubmit} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormHandleSubmit";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormValues";

interface IProps {
    showForm: boolean;
    initialValues: ILeadModalPriceConversationChatLeadFormValues;
    disabled: boolean;
    handleSubmit: ILeadModalPriceConversationChatLeadFormHandleSubmit;
    handleFormInteraction: () => void;
    handleChangeData: () => void;
}

export function LeadModalPriceConversationChatLeadForm(props: IProps) {
    const {initialValues, disabled, handleSubmit, handleFormInteraction, showForm, handleChangeData} = props;

    const {vendorId} = useOpenedLeadModalRequiredParams("property");
    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId);
    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);

    const form = useForm<ILeadModalPriceConversationChatLeadFormValues>({
        disabled,
        defaultValues: initialValues,
        resolver: yupResolver(formSchema),
        mode: "onBlur"
    });

    const onSubmit = async (formValues: ILeadModalPriceConversationChatLeadFormValues) => {
        handleSubmit(formValues, (error) => {
            //show form when logged user has blocked email
            if (isValidationError(error) && !showForm) {
                handleChangeData();
            }
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    form.setError(fieldName as keyof ILeadModalPriceConversationChatLeadFormValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        });
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} css={fields}>
                {(!showForm && !initialValues) || showForm ? (
                    <>
                        <InputField
                            name="name"
                            placeholder="Imię i nazwisko"
                            onClick={handleFormInteraction}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT}
                        />
                        <InputField name="email" placeholder="E-mail" onClick={handleFormInteraction} dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT} />
                        <PhoneFieldControlled
                            name="phone"
                            codes={codes ?? []}
                            onClick={handleFormInteraction}
                            placeholder="Numer telefonu"
                            maxMenuHeight={200}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        />
                    </>
                ) : (
                    <div css={filledData}>
                        <Text variant="mini_header">Twoje dane</Text>
                        <Text variant="body_copy_2">
                            Imię i nazwisko: <strong>{initialValues.name}</strong>
                            <br />
                            e-mail: <strong>{initialValues.email}</strong>
                            <br />
                            Telefon: <strong>{initialValues.phone.number}</strong>
                        </Text>
                        <Link css={linkDecoration} onClick={handleChangeData}>
                            Zmień dane
                        </Link>
                    </div>
                )}
                <div css={optins}>
                    {(showForm || (!showForm && !initialValues.new_property_notification_consent)) && (
                        <CheckboxControlled
                            name="new_property_notification_consent"
                            id="new_property_notification_consent"
                            labelContent={
                                <Text variant="info_txt_2">
                                    Chcę otrzymywać mailem lub SMSem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać.
                                </Text>
                            }
                            checkedMarkColor="#02D054"
                        />
                    )}
                    {(showForm || (!showForm && !initialValues.financing_services)) && (
                        <CheckboxControlled
                            name="financing_services"
                            id="financing_services"
                            labelContent={<Text variant="info_txt_2">Poproszę o przedstawienie oferty kredytu na to mieszkanie.</Text>}
                            checkedMarkColor="#02D054"
                        />
                    )}
                </div>
                <Button
                    variant="filled_primary"
                    type="submit"
                    css={button}
                    onClick={handleFormInteraction}
                    disabled={disabled}
                    isLoading={disabled}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {askForPriceleadButtonCopy}
                </Button>
                <RodoFormInfo vendor={vendorDetail} css={rodo} />
                <Text variant="info_txt_2" as="span" css={terms}>
                    Akceptuję{" "}
                    <Link href={termsOfService} size="inherit" target="_blank" css={linkDecoration}>
                        regulamin serwisu.
                    </Link>
                </Text>
            </form>
        </FormProvider>
    );
}

// Utils
const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()})
});

function isValidationError(error: unknown): error is {appError: IAppError} {
    return typeof error === "object" && error !== null && "appError" in error && (error.appError as IAppError).status === 400;
}

const termsOfService = rpAppLink.termsOfService.base();

// Styles
const fields = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
`;

const optins = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
    padding: ${calculateRemSize(1)} 0;
`;

const rodo = css`
    padding: 0;
    margin: 0 0 ${calculateRemSize(1)} 0;
`;

const terms = css`
    text-align: center;
`;

const linkDecoration = css`
    text-decoration: underline;
`;

const button = css`
    height: ${calculateRemSize(5)};
`;

const filledData = (theme: Theme) => css`
    background: ${theme.colors.background[100]};
    padding: ${calculateRemSize(2)};
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(1)};
    ${borderRadius(2)}
`;
